
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { Button } from '../Button';
import { Check, Phone, FileText, Share2, Mic, ArrowRight, Zap, Bot, Clock } from 'lucide-react';
import { Page } from '../../types';

interface ReadyToGoPageProps {
    onNavigate?: (page: Page) => void;
}

export const ReadyToGoPage: React.FC<ReadyToGoPageProps> = ({ onNavigate }) => {
    const { t } = useTranslation();

    const products = [
        {
            title: t('shared.ready_to_go.products.receptionist.title'),
            icon: Phone,
            description: t('shared.ready_to_go.products.receptionist.desc'),
            features: t('shared.ready_to_go.products.receptionist.features', { returnObjects: true }) as string[],
            price: t('shared.ready_to_go.products.receptionist.price'),
            color: "text-blue-400",
            bgColor: "bg-blue-400/10",
            borderColor: "border-blue-400/20"
        },
        {
            title: t('shared.ready_to_go.products.invoice.title'),
            icon: FileText,
            description: t('shared.ready_to_go.products.invoice.desc'),
            features: t('shared.ready_to_go.products.invoice.features', { returnObjects: true }) as string[],
            price: t('shared.ready_to_go.products.invoice.price'),
            color: "text-green-400",
            bgColor: "bg-green-400/10",
            borderColor: "border-green-400/20"
        },
        {
            title: t('shared.ready_to_go.products.social.title'),
            icon: Share2,
            description: t('shared.ready_to_go.products.social.desc'),
            features: t('shared.ready_to_go.products.social.features', { returnObjects: true }) as string[],
            price: t('shared.ready_to_go.products.social.price'),
            color: "text-purple-400",
            bgColor: "bg-purple-400/10",
            borderColor: "border-purple-400/20"
        },
        {
            title: t('shared.ready_to_go.products.meeting.title'),
            icon: Mic,
            description: t('shared.ready_to_go.products.meeting.desc'),
            features: t('shared.ready_to_go.products.meeting.features', { returnObjects: true }) as string[],
            price: t('shared.ready_to_go.products.meeting.price'),
            color: "text-orange-400",
            bgColor: "bg-orange-400/10",
            borderColor: "border-orange-400/20"
        }
    ];

    return (
        <div className="pt-32 pb-20 bg-rabbit-navy min-h-screen">
            <div className="max-w-7xl mx-auto px-6">

                {/* HEADER */}
                <Reveal>
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm">
                            <Zap size={16} className="text-yellow-400 fill-yellow-400" />
                            <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">{t('shared.ready_to_go.header.badge')}</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight">
                            {t('shared.ready_to_go.header.title_line1')} <span className="text-clever-teal">{t('shared.ready_to_go.header.title_line2')}</span>
                        </h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                            {t('shared.ready_to_go.header.subtitle')}
                        </p>
                    </div>
                </Reveal>

                {/* PRODUCTS GRID */}
                <div className="grid md:grid-cols-2 gap-8 mb-32">
                    {products.map((product, index) => (
                        <Reveal key={index} delay={index * 0.1}>
                            <div className={`h-full bg-[#0F172A] border ${product.borderColor} rounded-2xl p-8 md:p-10 hover:bg-[#151f38] transition-all duration-300 group flex flex-col`}>

                                <div className="flex justify-between items-start mb-8">
                                    <div className={`w-16 h-16 rounded-xl ${product.bgColor} flex items-center justify-center ${product.color}`}>
                                        <product.icon size={32} />
                                    </div>
                                    <div className={`px-4 py-1 rounded-full ${product.bgColor} border ${product.borderColor}`}>
                                        <span className={`text-xs font-bold font-mono ${product.color}`}>{product.price}</span>
                                    </div>
                                </div>

                                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-clever-teal transition-colors">
                                    {product.title}
                                </h3>
                                <p className="text-gray-400 mb-8 leading-relaxed text-lg">
                                    {product.description}
                                </p>

                                <ul className="space-y-4 mb-10 flex-1">
                                    {product.features.map((feature, i) => (
                                        <li key={i} className="flex items-center gap-3 text-sm text-gray-300">
                                            <Check size={16} className="text-clever-teal shrink-0" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Button
                                    onClick={() => {
                                        onNavigate?.('contact');
                                        window.scrollTo(0, 0);
                                    }}
                                    className="w-full justify-center group-hover:bg-clever-teal group-hover:text-white transition-all"
                                    variant="outline"
                                >
                                    {t('shared.ready_to_go.products.cta')} <ArrowRight size={16} className="ml-2" />
                                </Button>

                            </div>
                        </Reveal>
                    ))}
                </div>

                {/* COMPARISON / UPSELL */}
                <Reveal>
                    <div className="bg-gradient-to-br from-clever-teal/10 to-transparent border border-clever-teal/20 rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-clever-teal/10 blur-[100px] rounded-full pointer-events-none"></div>

                        <Bot size={48} className="text-clever-teal mx-auto mb-8 animate-bounce" />

                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">{t('shared.ready_to_go.upsell.title')}</h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
                            {t('shared.ready_to_go.upsell.desc')}
                        </p>

                        <div className="flex flex-col md:flex-row gap-4 justify-center">
                            <Button
                                onClick={() => {
                                    onNavigate?.('custom-solutions');
                                    window.scrollTo(0, 0);
                                }}
                                variant="primary"
                                className="py-4 px-8 text-lg"
                            >
                                {t('shared.ready_to_go.upsell.cta_custom')}
                            </Button>
                            <Button
                                onClick={() => {
                                    onNavigate?.('contact');
                                    window.scrollTo(0, 0);
                                }}
                                variant="outline"
                                className="py-4 px-8 text-lg bg-transparent border-gray-600 hover:border-white text-white"
                            >
                                {t('shared.ready_to_go.upsell.cta_audit')}
                            </Button>
                        </div>

                        <div className="mt-12 flex items-center justify-center gap-2 text-sm text-gray-500 font-mono">
                            <Clock size={14} />
                            <span>{t('shared.ready_to_go.upsell.time_info')}</span>
                        </div>

                    </div>
                </Reveal>

            </div>
        </div>
    );
};
